import pt from '../locales/pt';

const SHORT_MONTHS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

export function shortMonthLabel(month) {
  const index = Number(month) - 1;
  return SHORT_MONTHS[index] ?? String(month);
}

export function dutiesForMonth(items = []) {
  return items.filter((item) => (item.item_type || 'occasional') === 'occasional');
}

export function goalsForMonth(items = []) {
  return items.filter((item) => item.item_type === 'goal');
}

export function monthlySmoothingAmount(annualTotal) {
  const total = Number(annualTotal || 0);
  if (!total) return 0;
  return Math.round((total / 12) * 100) / 100;
}

export function reservedPct(accumulated, total) {
  const acc = Number(accumulated || 0);
  const tgt = Number(total || 0);
  if (!tgt) return 0;
  return Math.min(100, Math.round((acc / tgt) * 100));
}

export function isCalendarEmpty(data) {
  if (!data) return true;
  const occasionalCount = Number(data.occasional_count ?? 0);
  const dreamCount = Number(data.dream_count ?? 0);
  if (occasionalCount > 0 || dreamCount > 0) return false;
  return Number(data.annual_total || 0) === 0;
}

export function defaultSelectedMonth(year, months = [], today = new Date()) {
  if (!months.length) return today.getMonth() + 1;
  const currentYear = today.getFullYear();
  if (year === currentYear) return today.getMonth() + 1;

  const withItems = months.find((month) => (month.items || []).length > 0);
  return withItems?.month ?? 1;
}

/**
 * Graded peak intensity for duty months (0 = empty/no duty, 1–3 = leve→pico).
 * Relative to months that have duty_total > 0.
 */
export function computeDutyIntensityLevels(months = []) {
  const dutyTotals = months.map((month) => ({
    month: month.month,
    total: Number(month.duty_total ?? month.total ?? 0),
  }));
  const positive = dutyTotals.filter((entry) => entry.total > 0);
  const levels = new Map();

  if (!positive.length) {
    dutyTotals.forEach((entry) => levels.set(entry.month, 0));
    return levels;
  }

  const sortedAsc = [...positive].sort((a, b) => a.total - b.total);
  const max = sortedAsc[sortedAsc.length - 1].total;
  const min = sortedAsc[0].total;

  positive.forEach((entry) => {
    if (positive.length === 1) {
      levels.set(entry.month, 2);
      return;
    }
    if (max === min) {
      levels.set(entry.month, 2);
      return;
    }
    const ratio = (entry.total - min) / (max - min);
    if (ratio >= 0.75 || entry.total === max) {
      levels.set(entry.month, 3);
    } else if (ratio >= 0.4) {
      levels.set(entry.month, 2);
    } else {
      levels.set(entry.month, 1);
    }
  });

  // Ensure at least the top month is peak when there are 2+ positive months
  if (positive.length >= 2) {
    const top = [...positive].sort((a, b) => b.total - a.total)[0];
    levels.set(top.month, 3);
  }

  dutyTotals.forEach((entry) => {
    if (!levels.has(entry.month)) levels.set(entry.month, 0);
  });

  return levels;
}

export function monthGridSubtitle(month, items = []) {
  const duties = dutiesForMonth(items);
  const dreams = goalsForMonth(items);
  const dutyCount = duties.length;
  const dreamCount = dreams.length;

  if (dutyCount === 0 && dreamCount === 0) return null;
  if (dutyCount > 0 && dreamCount > 0) {
    return pt.calendar.yearGrid.subtitleMixed
      .replace('{duties}', String(dutyCount))
      .replace('{dreams}', String(dreamCount));
  }
  if (dreamCount > 0) {
    return dreamCount === 1
      ? pt.calendar.yearGrid.oneDream
      : pt.calendar.yearGrid.dreamCount.replace('{count}', String(dreamCount));
  }
  if (dutyCount === 1) return duties[0].name;
  if (dutyCount === 2) return `${duties[0].name} + ${duties[1].name}`;
  return pt.calendar.itemCount.replace('{count}', String(dutyCount));
}

export function buildMonthGridMeta(months = []) {
  const intensityByMonth = computeDutyIntensityLevels(months);
  return months.map((month) => {
    const items = month.items || [];
    const dutyTotal = Number(month.duty_total ?? month.total ?? 0);
    const dreams = goalsForMonth(items);
    const intensity = dutyTotal > 0 ? intensityByMonth.get(month.month) ?? 0 : 0;
    const hasDream = Boolean(month.has_dream) || dreams.length > 0;
    return {
      month: month.month,
      monthName: month.month_name,
      shortLabel: shortMonthLabel(month.month),
      dutyTotal,
      isEmpty: dutyTotal === 0 && items.length === 0,
      intensity,
      isHeavy: intensity >= 2,
      hasDream,
      subtitle: monthGridSubtitle(month, items),
    };
  });
}

/**
 * Monthly smoothing pace for a single duty when API omits monthly_amount.
 * Spreads remaining balance over months left in the year (min 1).
 */
export function estimateOccasionalMonthly(item, today = new Date()) {
  const fromApi = Number(item?.monthly_amount);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;

  const amount = Number(item?.amount || 0);
  const accumulated = Number(item?.accumulated || 0);
  const remaining = Math.max(amount - accumulated, 0);
  if (remaining <= 0) return 0;

  const targetMonth = Number(item?.month || item?.target_month || 0);
  const currentMonth = today.getMonth() + 1;
  let monthsLeft = 1;
  if (targetMonth >= 1 && targetMonth <= 12) {
    monthsLeft = Math.max(targetMonth - currentMonth + 1, 1);
  } else {
    monthsLeft = Math.max(12 - currentMonth + 1, 1);
  }
  return Math.round((remaining / monthsLeft) * 100) / 100;
}

export function formatPotProgressLine(item, today = new Date()) {
  const amount = Number(item.amount || 0);
  const accumulated = Number(item.accumulated || 0);
  const remaining = Math.max(amount - accumulated, 0);
  const pct = reservedPct(accumulated, amount);

  if (pct >= 100) {
    return {
      pct: 100,
      complete: true,
      primary: pt.calendar.monthDetail.potComplete,
      secondary: pt.calendar.monthDetail.potAmounts
        .replace('{accumulated}', accumulated.toFixed(0))
        .replace('{amount}', amount.toFixed(0)),
    };
  }

  const monthly = estimateOccasionalMonthly(item, today);
  return {
    pct,
    complete: false,
    primary: pt.calendar.monthDetail.potPartial
      .replace('{accumulated}', accumulated.toFixed(0)),
    secondary: pt.calendar.monthDetail.potRemaining
      .replace('{remaining}', remaining.toFixed(0))
      .replace('{monthly}', monthly > 0 ? monthly.toFixed(0) : '—'),
  };
}
